import React, { useEffect, useRef, useState } from 'react'

import { ChevronDownIcon, ChevronRightIcon, PlusIcon } from 'lucide-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '~/client/src/shared/components/shadcn-ui/input-group'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import HierarchyOptionNamePopover from './HierarchyOptionNamePopover'
import {
  HierarchyNameDraftValidation,
  HierarchyOption,
  HierarchyRow,
} from './HierarchyPropertyConfig.types'

interface IHierarchyOptionRowProps {
  row: HierarchyRow
  isSelected: boolean
  isExpanded: boolean
  dragHandle: React.ReactNode
  onSelectedChange: (value: string, checked: boolean) => void
  onExpandedToggle: (value: string) => void
  onUpdateOption: (
    index: number,
    changes: Partial<Pick<HierarchyRow['option'], 'label' | 'value'>>,
  ) => void
  onNameManuallyEdited: (value: string) => void
  onLabelBlur: (index: number) => void
  getNameDraftValidation: (
    index: number,
    draft: string,
  ) => HierarchyNameDraftValidation
  onRestoreAndApply: (index: number, deletedOption: HierarchyOption) => void
  onInsertBelow?: (value: string) => void
  autoFocusValue?: string | null
  onAutoFocusHandled?: () => void
}

const gridTemplateColumns = '24px 40px minmax(0, 1fr)'

const HierarchyOptionRow: React.FC<IHierarchyOptionRowProps> = ({
  row,
  isSelected,
  isExpanded,
  dragHandle,
  onSelectedChange,
  onExpandedToggle,
  onUpdateOption,
  onNameManuallyEdited,
  onLabelBlur,
  getNameDraftValidation,
  onRestoreAndApply,
  onInsertBelow,
  autoFocusValue,
  onAutoFocusHandled,
}) => {
  // Local edit buffer for the label. The committed option list re-parses on
  // every keystroke and drops empty-label entries, so an empty label is only
  // held here (never persisted) to keep the row alive while retyping; on blur
  // an empty draft reverts to the committed label. Row identity is keyed by
  // option.value, so this state re-initialises when the option changes.
  const [labelDraft, setLabelDraft] = useState(row.option.label)

  const labelCellRef = useRef<HTMLDivElement>(null)
  const shouldAutoFocus = autoFocusValue === row.option.value

  // A just-created option focuses and selects its label so the placeholder
  // name can be typed over immediately; clear the request so scrolling this
  // row back into view later doesn't steal focus.
  useEffect(() => {
    if (!shouldAutoFocus) return
    const input = labelCellRef.current?.querySelector('input')
    if (input) {
      input.focus()
      input.select()
    }
    onAutoFocusHandled?.()
  }, [shouldAutoFocus, onAutoFocusHandled])

  return (
    <div
      className={`tw:group tw:grid tw:items-center tw:border-0 tw:border-b tw:border-solid tw:border-border tw:px-2 tw:py-1.5 tw:last:border-b-0 ${
        isSelected ? 'tw:bg-muted' : 'tw:bg-background'
      }`}
      style={{ gridTemplateColumns }}
    >
      <div
        className={`tw:flex tw:items-center tw:justify-center ${
          isSelected
            ? 'tw:opacity-100'
            : 'tw:opacity-0 tw:group-hover:opacity-100 tw:focus-within:opacity-100'
        }`}
      >
        {dragHandle}
      </div>
      <div className="tw:flex tw:items-center tw:justify-center">
        <Checkbox
          checked={isSelected}
          aria-label={Localization.translator.selectOption(row.option.label)}
          onCheckedChange={checked =>
            onSelectedChange(row.option.value, checked === true)
          }
        />
      </div>
      <div
        ref={labelCellRef}
        className="tw:relative tw:flex tw:items-center tw:gap-1 tw:pr-3"
        style={{ paddingLeft: row.depth * 28 }}
      >
        {Array.from({ length: row.depth }).map((_, depthIndex) => (
          <span
            key={depthIndex}
            className="tw:absolute tw:top-[-7px] tw:bottom-[-7px] tw:w-px tw:bg-border"
            style={{ left: depthIndex * 28 + 14 }}
            aria-hidden="true"
          />
        ))}
        {row.hasChildren ? (
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={
              isExpanded
                ? Localization.translator.collapseOption
                : Localization.translator.expandOption
            }
            onClick={() => onExpandedToggle(row.option.value)}
          >
            {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
          </Button>
        ) : (
          <span className="tw:size-6 tw:shrink-0" aria-hidden="true" />
        )}
        <InputGroup className="tw:bg-background">
          <InputGroupInput
            value={labelDraft}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const nextLabel = event.target.value
              setLabelDraft(nextLabel)
              // Only the label syncs live. The internal value is (re)derived on
              // blur by onLabelBlur; deriving it per keystroke would change
              // row.option.value — the row's React key — and remount the input
              // mid-typing, dropping focus.
              if (nextLabel.trim()) {
                onUpdateOption(row.index, { label: nextLabel })
              }
            }}
            onBlur={() => {
              if (!labelDraft.trim()) setLabelDraft(row.option.label)
              onLabelBlur(row.index)
            }}
          />
          <InputGroupAddon align="inline-end">
            <HierarchyOptionNamePopover
              row={row}
              getNameDraftValidation={getNameDraftValidation}
              onUpdateOption={onUpdateOption}
              onNameManuallyEdited={onNameManuallyEdited}
              onRestoreAndApply={onRestoreAndApply}
            />
          </InputGroupAddon>
        </InputGroup>
        {onInsertBelow && (
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={Localization.translator.addOptionBelow(
              row.option.label,
            )}
            className="tw:shrink-0 tw:opacity-0 tw:group-hover:opacity-100 tw:focus-visible:opacity-100"
            onClick={() => onInsertBelow(row.option.value)}
          >
            <PlusIcon />
          </Button>
        )}
      </div>
    </div>
  )
}

export { gridTemplateColumns }
export default HierarchyOptionRow
